import { useCallback, useEffect, useRef } from 'react';
import type {
    FollowSelectorSection as FollowSelectorSectionType,
    FollowSelectorItem as FollowSelectorItemType,
} from '../../types/follow-selector';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { FollowSelectorSearch } from './FollowSelectorSearch';
import { FollowSelectorSection } from './FollowSelectorSection';
import { FollowSelectorFooter } from './FollowSelectorFooter';
import { FollowSelectorEmpty } from './FollowSelectorEmpty';
import { Spinner } from '@rocco/components/spinner';
import { Button } from '@rocco/components/button';
import { AlertCircle, Loader2, X } from 'lucide-react';

/**
 * Fixed desktop height for the scrollable content area in pixels.
 * Keeping this region stable prevents desktop popover re-positioning
 * when content switches between loading/empty/results states.
 */
const CONTENT_MAX_HEIGHT = 320;

interface FollowSelectorContentProps {
    /** Sections to display in grouped view */
    sections: FollowSelectorSectionType[];
    /** Callback when an item is selected */
    onSelect: (item: FollowSelectorItemType) => void;
    /** Callback when a new board is created */
    onBoardCreated: (board: DesignBoardViewModel) => void;
    /** Callback when search query changes */
    onSearch?: (query: string) => void;
    /** Whether data is currently loading */
    isLoading?: boolean;
    /** Whether pin membership is loading */
    membershipLoading?: boolean;
    /** Whether an error occurred */
    hasError?: boolean;
    /** Callback to retry after an error */
    onRetry?: () => void;
    /** Placeholder text for search input */
    searchPlaceholder?: string;
    /** IDs of items currently being processed (saving) */
    processingItemIds?: number[];
    /** Whether there are more pages to load */
    hasNextPage?: boolean;
    /** Callback to load more items */
    onLoadMore?: () => void;
    /** Whether currently loading more items */
    isLoadingMore?: boolean;
    /** Current search query */
    searchQuery?: string;
    /** Whether search is in progress */
    isSearching?: boolean;
    /**
     * When true, the scrollable content area expands to fill available
     * height (using flex-1) instead of being capped at CONTENT_MAX_HEIGHT.
     * Used when rendered inside a mobile drawer.
     */
    fullHeight?: boolean;
    /**
     * Callback to close the selector.
     * When provided, a close button is rendered in the header.
     */
    onClose?: () => void;
}

/**
 * Main content container for the FollowSelector.
 * Handles search input and displays sections or search results.
 */
export const FollowSelectorContent = ({
    sections,
    onSelect,
    onBoardCreated,
    onSearch,
    isLoading = false,
    membershipLoading = false,
    hasError = false,
    onRetry,
    searchPlaceholder,
    processingItemIds = [],
    hasNextPage = false,
    onLoadMore,
    isLoadingMore = false,
    searchQuery = '',
    isSearching = false,
    fullHeight = false,
    onClose,
}: FollowSelectorContentProps) => {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

    const handleSearchChange = useCallback(
        (value: string) => {
            onSearch?.(value);
        },
        [onSearch],
    );

    const hasItems = sections.some((s) => s.items.length > 0);

    // Prevent clicks inside popover from bubbling up to parent elements (e.g., tile navigation)
    const handleContentClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    // Prevent right-click from bubbling up to parent admin context menu
    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    // Infinite scroll: load more when sentinel is visible
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollArea = scrollAreaRef.current;

        if (
            !sentinel ||
            !scrollArea ||
            !hasNextPage ||
            !onLoadMore ||
            isLoadingMore
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting && hasNextPage && !isLoadingMore) {
                    onLoadMore();
                }
            },
            {
                root: scrollArea,
                rootMargin: '100px', // Start loading before reaching the bottom
                threshold: 0.1,
            },
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasNextPage, onLoadMore, isLoadingMore]);

    return (
        <div
            className={`flex w-full flex-col ${fullHeight ? 'h-full' : ''}`}
            onClick={handleContentClick}
            onContextMenu={handleContextMenu}
        >
            {/* Header - shrink-0 prevents compression when keyboard opens on mobile */}
            <div className="relative shrink-0 px-4.5 pb-4 pt-5 text-center">
                <h2 className="text-lg font-semibold text-gray-900">
                    {BOARD_TERMS.saveTo}
                </h2>
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xs absolute right-4 top-5 p-0.5 text-gray-500 transition-colors hover:text-gray-900"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* Search */}
            <FollowSelectorSearch
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                isSearching={isSearching}
                autoFocus={!fullHeight}
            />

            {/* Content */}
            <div
                ref={scrollAreaRef}
                className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto overscroll-contain"
                style={
                    fullHeight
                        ? undefined
                        : {
                              minHeight: `${CONTENT_MAX_HEIGHT}px`,
                              maxHeight: `${CONTENT_MAX_HEIGHT}px`,
                          }
                }
            >
                {hasError ? (
                    <div className="flex flex-col items-center justify-center gap-4 py-8">
                        <AlertCircle className="h-10 w-10 text-gray-400" />
                        <div className="text-center">
                            <p className="text-sm font-medium text-gray-900">
                                {BOARD_TERMS.failedToLoad}
                            </p>
                            <p className="text-xs text-gray-500">
                                Please try again
                            </p>
                        </div>
                        {onRetry && (
                            <Button
                                size="narrow"
                                color="primary"
                                onClick={onRetry}
                            >
                                Retry
                            </Button>
                        )}
                    </div>
                ) : isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Spinner />
                    </div>
                ) : hasItems ? (
                    <div className="">
                        {sections.map((section) => (
                            <FollowSelectorSection
                                key={section.id}
                                section={section}
                                onSelect={onSelect}
                                processingItemIds={processingItemIds}
                                membershipLoading={membershipLoading}
                            />
                        ))}

                        {/* Loading more indicator */}
                        {isLoadingMore && (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                        )}

                        {/* Sentinel for infinite scroll */}
                        {hasNextPage && !isLoadingMore && (
                            <div
                                ref={loadMoreSentinelRef}
                                className="h-1"
                                aria-hidden="true"
                            />
                        )}
                    </div>
                ) : (
                    <FollowSelectorEmpty isSearching={searchQuery.length > 0} />
                )}
            </div>

            {/* Footer */}
            <FollowSelectorFooter onBoardCreated={onBoardCreated} />
        </div>
    );
};
